// Floor-plan document model for the m3 calculator (laskuri).
// Pure JS - no React, no DOM. All world units are METERS; the UI converts
// thickness/drain depth to mm for display. y grows DOWNWARD (screen convention).
// Rect corner order: [nw, ne, se, sw]. Triangle thickness.values[i] <-> points[i].
export const PLAN_VERSION = 1;

export const LIMITS = {
  minSize: 0.1, // m - smallest shape edge / circle diameter
  maxSize: 100, // m
  minThickness: 0.01, // m (10 mm)
  maxThickness: 0.5, // m (500 mm)
  minDrainDepth: 0.001, // m (1 mm)
  maxDrainDepth: 0.2, // m (200 mm)
  minDrainRadius: 0.1, // m
  maxDrainRadius: 5, // m
  maxHukkavaraPct: 50,
  minPillarDiameter: 0.05, // m (5 cm)
  maxPillarDiameter: 2, // m (200 cm)
  minPillarHeight: 0.1, // m
  maxPillarHeight: 10, // m
};

const uid = () => crypto.randomUUID().slice(0, 8);

export function createEmptyPlan() {
  return { v: PLAN_VERSION, unit: "m", shapes: [], drains: [], hukkavaraPct: 10 };
}

export function newRect({ x = 0, y = 0, w = 4, h = 3, thickness = 0.1 } = {}) {
  return { id: uid(), type: "rect", x, y, w, h, rot: 0, thickness: { mode: "uniform", value: thickness } };
}

export function newTri({ points = [[0, 0], [3, 0], [0, 3]], thickness = 0.1 } = {}) {
  return { id: uid(), type: "tri", points: points.map((p) => [...p]), thickness: { mode: "uniform", value: thickness } };
}

export function newCircle({ cx = 0, cy = 0, r = 1.5, thickness = 0.1 } = {}) {
  return { id: uid(), type: "circle", cx, cy, r, thickness: { mode: "uniform", value: thickness } };
}

/** Round cast column - an ADDITIVE cylinder (pi*r^2*height), never part of the slab union. */
export function newPillar({ cx = 0, cy = 0, r = 0.15, height = 2.7 } = {}) {
  return { id: uid(), type: "pillar", cx, cy, r, height };
}

export function newDrain({ x = 0, y = 0, depth = 0.02, radius = 0.6 } = {}) {
  return { id: uid(), x, y, depth, radius };
}

export function cloneShape(shape) {
  return { ...JSON.parse(JSON.stringify(shape)), id: uid() };
}

const isNum = (n) => typeof n === "number" && Number.isFinite(n);

function validThickness(t, cornerCount) {
  if (!t || typeof t !== "object") return false;
  const inRange = (v) => isNum(v) && v >= LIMITS.minThickness && v <= LIMITS.maxThickness;
  if (t.mode === "uniform") return inRange(t.value);
  if (t.mode === "corners") {
    return cornerCount > 0 && Array.isArray(t.values) && t.values.length === cornerCount && t.values.every(inRange);
  }
  return false;
}

function validShape(s) {
  if (!s || typeof s.id !== "string") return false;
  if (s.type === "rect") {
    return [s.x, s.y, s.w, s.h].every(isNum) &&
      s.w >= LIMITS.minSize && s.w <= LIMITS.maxSize &&
      s.h >= LIMITS.minSize && s.h <= LIMITS.maxSize &&
      validThickness(s.thickness, 4);
  }
  if (s.type === "tri") {
    return Array.isArray(s.points) && s.points.length === 3 &&
      s.points.every((p) => Array.isArray(p) && p.length === 2 && p.every(isNum)) &&
      validThickness(s.thickness, 3);
  }
  if (s.type === "circle") {
    return [s.cx, s.cy, s.r].every(isNum) &&
      s.r >= LIMITS.minSize / 2 && s.r <= LIMITS.maxSize / 2 &&
      s.thickness?.mode === "uniform" && validThickness(s.thickness, 0);
  }
  if (s.type === "pillar") {
    return [s.cx, s.cy, s.r, s.height].every(isNum) &&
      s.r >= LIMITS.minPillarDiameter / 2 && s.r <= LIMITS.maxPillarDiameter / 2 &&
      s.height >= LIMITS.minPillarHeight && s.height <= LIMITS.maxPillarHeight;
  }
  return false;
}

function validDrain(d) {
  return Boolean(d) && typeof d.id === "string" && isNum(d.x) && isNum(d.y) &&
    isNum(d.depth) && d.depth >= LIMITS.minDrainDepth && d.depth <= LIMITS.maxDrainDepth &&
    isNum(d.radius) && d.radius >= LIMITS.minDrainRadius && d.radius <= LIMITS.maxDrainRadius;
}

export function validatePlan(plan) {
  if (!plan || typeof plan !== "object") return { ok: false, errors: ["not an object"] };
  const errors = [];
  if (plan.v !== PLAN_VERSION) errors.push(`unsupported version ${plan.v}`);
  if (!Array.isArray(plan.shapes)) errors.push("shapes not an array");
  else plan.shapes.forEach((s, i) => { if (!validShape(s)) errors.push(`shape[${i}] invalid`); });
  if (!Array.isArray(plan.drains)) errors.push("drains not an array");
  else plan.drains.forEach((d, i) => { if (!validDrain(d)) errors.push(`drain[${i}] invalid`); });
  if (!isNum(plan.hukkavaraPct) || plan.hukkavaraPct < 0 || plan.hukkavaraPct > LIMITS.maxHukkavaraPct) {
    errors.push("hukkavaraPct invalid");
  }
  return { ok: errors.length === 0, errors };
}

export function serializePlan(plan) {
  return JSON.stringify(plan);
}

/** Parse + migrate + validate. Returns a plan or null - NEVER throws. */
export function parsePlan(raw) {
  try {
    const obj = typeof raw === "string" ? JSON.parse(raw) : raw;
    const migrated = migratePlan(obj);
    if (!migrated) return null;
    return validatePlan(migrated).ok ? migrated : null;
  } catch {
    return null;
  }
}

function migratePlan(obj) {
  if (!obj || typeof obj !== "object") return null;
  if (obj.v === PLAN_VERSION) return obj;
  return null; // unknown/future version - caller falls back to an empty plan
}

/**
 * Fixed-decimal formatting: formatFi(12.34, 1) === "12,3" (Finnish comma,
 * the default). Deliberately NOT routed through `formatNumber`/Intl even for
 * the `lang="en"` case: `Intl.NumberFormat` inserts a thousands-grouping
 * separator above 1000 (a plausible floor-plan area at LIMITS.maxSize=100m
 * per edge) that this toFixed()-based algorithm never produced, so
 * delegating would risk changing Finnish output — forbidden here. Finnish
 * stays the exact original algorithm; English just skips the comma-swap.
 */
export function formatFi(n, decimals = 1, lang = "fi") {
  if (!Number.isFinite(n)) return "–";
  const fixed = n.toFixed(decimals);
  return lang === "en" ? fixed : fixed.replace(".", ",");
}

/**
 * One-line summary of a stored floor plan — used as the provider recap row
 * and the PNG snapshot caption. Reads the denormalized `computed` block;
 * degrades to counts-only when absent. Null when empty.
 *
 * `lang` locale-formats the numbers (see `formatFi`). `recommendedWord` is
 * the already-resolved translation of "suositus" — this is a pure module
 * with no React/i18n-bundle access (see CLAUDE.md), so the caller must
 * resolve it via `t("laskuri.floorPlanSummary.recommendedWord")` and pass
 * it in; defaults to the Finnish word so any caller that forgets keeps
 * today's behaviour. The `alue`/`pilari`/`kaivo` counts stay hardcoded
 * Finnish nouns — same pre-existing, separately-tracked gap as this
 * component's other un-extracted strings (see RequestRecap.jsx's note).
 */
export function floorPlanSummary(fp, lang = "fi", recommendedWord = "suositus") {
  const shapes = fp?.shapes?.length ?? 0;
  if (!shapes) return null;
  const pillars = fp.shapes.filter((s) => s.type === "pillar").length;
  const areas = shapes - pillars;
  const drains = fp.drains?.length ?? 0;
  const head = [
    areas === 0 ? "" : areas === 1 ? "1 alue" : `${areas} aluetta`,
    pillars === 0 ? "" : pillars === 1 ? "1 pilari" : `${pillars} pilaria`,
    drains === 0 ? "" : drains === 1 ? "1 kaivo" : `${drains} kaivoa`,
  ].filter(Boolean).join(", ");
  const c = fp.computed;
  if (!c || ![c.m2, c.m3Exact, c.m3Recommended].every(Number.isFinite)) return head;
  return `${head} — ${formatFi(c.m2, 1, lang)} m² · ${formatFi(c.m3Exact, 2, lang)} m³ (${recommendedWord} ${formatFi(c.m3Recommended, 1, lang)} m³)`;
}
